import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ensureUserProvisioned, fetchMyProfile } from '../api/users';
import { ApiError } from '../api/client';
import { snapshotMigrationConfig } from '../utils/migrationConfig';

// ════════════════════════════════════════════════════════════════════════════
// DE DÓNDE SALE EL PERFIL
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ EL PERFIL NUNCA HA SIDO EN TIEMPO REAL, Y ESO ES LO QUE HACE QUE ESTE    ║
// ║ CAMBIO NO PIERDA NADA.                                                   ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Abajo hay un `getDoc`, no un `onSnapshot`: una lectura ÚNICA por arranque de
// sesión, más las que pida `refreshProfile()` a mano. Cuando un admin suspende a
// alguien, `SuspensionGate` no se entera hasta que esa persona recarga o algo
// llama a `refreshProfile`. Leerlo del API se comporta EXACTAMENTE igual, así que
// no hay reactividad que reponer y NO se añade ningún sondeo: inventar un
// `setInterval` aquí sería estrenar en la migración un comportamiento que la app
// nunca tuvo, y pagarlo con una petición por usuario y por minuto.
//
// (Si algún día el corte de un suspendido tiene que ser inmediato, el sitio es
// esta función y vale para las dos bases — no es deuda de Postgres.)
//
// ─── SI EL API NO CONTESTA, SE LEE FIRESTORE ────────────────────────────────
//
// 🔴 Un perfil que no carga NO es "la pantalla sin datos": es `userProfile` en
// `null`, y entonces `realRole` cae a `'client'`. Un fallo de red mandaría al
// dueño de un taller a la app de cliente y —peor— dejaría a un SUSPENDIDO
// navegando, porque `suspended` llega `undefined` y el gate no cierra. Entre un
// perfil de Firestore que puede estar algo atrasado y NINGÚN perfil, gana el
// atrasado: conserva el rol, conserva la suspensión y conserva el modo sin
// conexión (Firestore tiene caché en IndexedDB; el API no).
//
// El 404 se trata aparte a propósito: no es una avería, es una cuenta que aún no
// existe en Postgres. Ahí hay que leer Firestore de todas formas —para enseñar
// algo Y para poder darla de alta— y en el siguiente arranque ya responde el API.
// ════════════════════════════════════════════════════════════════════════════

async function leerDeFirestore(uid) {
  const userDoc = await getDoc(doc(db, 'users', uid));
  return userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null;
}

// ─── EL PLAZO DE ESTA LECTURA ES PROPIO, Y MÁS CORTO QUE EL GENERAL ─────────
//
// 🔴 `App.js` no pinta NADA mientras `loading` es true, y `loading` no baja hasta
// que este perfil se resuelve. Con el plazo general del cliente del API
// (`API_TIMEOUT_MS`, 12 s) eso son doce segundos de pantalla en blanco cada vez
// que el API no contesta — y sin reintentos, así que el usuario ve la app rota
// sin ninguna señal de que está pasando algo.
//
// Debajo NO hay comportamiento nuevo: el camino de Firestore ya existe y ya está
// razonado (perfil algo atrasado > ningún perfil). Lo único que cambia es CUÁNDO
// se alcanza. Cinco segundos es más de lo que tarda una respuesta buena desde
// República Dominicana (0.26-0.42 s con el servidor caliente, un orden de
// magnitud de margen) y bastante menos de lo que una persona aguanta mirando una
// pantalla vacía.
//
// ⚠️ NO se mueve `setLoading(false)` delante del perfil. Con `userProfile` en
// `null`, `realRole` cae a `'client'`: el dueño de un taller vería un parpadeo de
// la app de cliente y —peor— `suspended` llegaría `undefined` y el gate de
// sanción no cerraría. Eso sería cambiar lentitud por un agujero.
//
// El plazo se exporta para que la prueba lo NOMBRE en vez de repetir el número.
export const PERFIL_PLAZO_MS = 5000;
// Cuántas veces se le pregunta al API por el perfil antes de rendirse (ver `cargarPerfil`).
export const PERFIL_INTENTOS = 2;

/**
 * `promesa`, pero rendida al cabo de `ms`.
 *
 * Se corre aquí y no dentro del cliente del API a propósito: `fetchMyProfile` no
 * propaga `timeoutMs` (solo `signal`), y esto tiene que valer aunque la llamada
 * de abajo cambie de forma. El perdedor NO queda como rechazo suelto: `race` se
 * suscribe a las dos promesas, así que un fallo tardío del API lo consume ella.
 */
function conPlazo(promesa, ms) {
  let timer;
  const plazo = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new ApiError(`El perfil no llegó en ${ms} ms.`, { code: 'timeout' })),
      ms,
    );
  });
  return Promise.race([promesa, plazo]).finally(() => clearTimeout(timer));
}

export async function cargarPerfil(uid) {
  let destino = 'firestore';
  try {
    const snap = await snapshotMigrationConfig();
    destino = snap.readsFrom('users');
  } catch (_) { /* no lanza, pero si lo hiciera el camino seguro es Firestore */ }

  if (destino === 'postgres') {
    // Dos intentos al API antes de rendirse: el primero suele chocar con el arranque
    // en frío del servidor (minInstances 0) y el segundo ya lo encuentra caliente.
    // Un 404 NO se reintenta: es una cuenta que aún no está en Postgres.
    let fallo = null;
    for (let intento = 0; intento < PERFIL_INTENTOS; intento++) {
      try {
        const perfil = await conPlazo(fetchMyProfile(), PERFIL_PLAZO_MS);
        // Un 200 sin cuerpo no es un perfil: se cae al camino viejo.
        if (perfil) return { data: perfil, desdeApi: true };
        fallo = null;
        break;
      } catch (err) {
        fallo = err;
        if (err?.status === 404) { fallo = null; break; }
      }
    }
    if (fallo) {
      console.warn('[auth] el API no devolvió el perfil; se lee de Firestore:', fallo?.message || fallo);
      // Firestore con documento = perfil algo atrasado, que conserva rol y suspensión.
      // Firestore SIN documento (o que también falla) NO prueba que la cuenta no
      // exista: las cuentas dadas de alta por el API no tienen documento allí. Eso es
      // «no se pudo leer», no «sin perfil» — solo un 404 real cuenta como sin perfil.
      let data = null;
      try { data = await leerDeFirestore(uid); } catch (_) { /* se trata como sin datos */ }
      if (!data) {
        throw new ApiError('No se pudo leer el perfil.', { code: 'profile_unavailable' });
      }
      return { data, desdeApi: false };
    }
  }

  return { data: await leerDeFirestore(uid), desdeApi: false };
}

// ════════════════════════════════════════════════════════════════════════════
// LA CÁSCARA FRÍA — lo único que se pinta con datos de la visita anterior
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ AQUÍ SE TRAZA UNA LÍNEA DE SEGURIDAD, Y ESTÁ TRAZADA POR CONSTRUCCIÓN:    ║
// ║ lo que no se guarda no se puede pintar por error mañana.                  ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// El problema medido: la app no pinta NADA hasta que contesta el servidor, así
// que la primera visita son ~2,5 s de pantalla en blanco. La respuesta NO es
// abrir los gates antes de tiempo: es tener algo honesto que enseñar mientras
// se espera. Para eso basta con saber cómo se llama la persona y qué foto tiene.
//
// ─── QUÉ SE GUARDA (y por qué es seguro) ────────────────────────────────────
//
//   `nombre`  → para saludar.
//   `foto`    → para el avatar.
//
// Las dos son identidad COSMÉTICA: no conceden ni deniegan nada, y quien tiene
// el aparato desbloqueado con la sesión abierta las vería igual medio segundo
// después. Pintarlas antes no expone nada nuevo.
//
// ─── QUÉ NO SE GUARDA, JAMÁS ────────────────────────────────────────────────
//
//   `role`, `suspended`, `suspensionReason`, `workshopId`, `idVerified`,
//   `founder`, `subscription*`… o sea TODO lo que una pantalla podría usar para
//   decidir qué enseñar o qué permitir.
//
// 🔴 Y no se guardan de verdad: esta función lee DOS campos del perfil y
// descarta el resto. No es una lista de exclusión que alguien pueda olvidar
// ampliar cuando el perfil crezca — es una lista de INCLUSIÓN. Aunque mañana
// alguien enchufe `cascara` en un sitio descuidado, ahí dentro no hay ni rol ni
// sanción que filtrar. El gate de suspensión sigue esperando al perfil de
// verdad (`profileLoaded`), sin excepción; ver `SuspensionGate` en App.js.
//
// ─── POR uid, SIEMPRE ───────────────────────────────────────────────────────
//
// Se guarda el uid dentro y se compara al leer. Sin eso, cambiar de cuenta en el
// mismo aparato saludaría con el nombre del anterior — la misma clase de fallo
// que el guard `vigente()` de abajo existe para cerrar.
//
// ─── EL PLAZO: 30 DÍAS ──────────────────────────────────────────────────────
//
// Ni corto ni eterno. Corto (una semana) dejaría fuera a quien abre la app una
// vez al mes, que es justo el uso normal de esta app: el carro no se atiende a
// diario. Eterno guardaría un nombre y una foto de hace dos años en un aparato
// que pudo cambiar de manos, y con más probabilidad de estar equivocados (la
// gente se cambia la foto) que de servir. A los 30 días se descarta y se vuelve
// al arranque de siempre: lo único que se pierde es el saludo.
//
// ─── `localStorage` PUEDE LANZAR ────────────────────────────────────────────
//
// Modo privado, cuota llena, políticas del navegador. Las tres funciones tragan
// el fallo: esto es un adorno del arranque y no puede tumbarlo. Sin cáscara la
// app arranca exactamente como antes.
// ════════════════════════════════════════════════════════════════════════════

export const CASCARA_KEY = 'mekafix_cascara';
export const CASCARA_PLAZO_MS = 30 * 24 * 60 * 60 * 1000;   // 30 días

/** Guarda SOLO nombre y foto del perfil recién resuelto, a nombre de `uid`. */
export function guardarCascara(uid, perfil) {
  if (!uid || !perfil) return;
  try {
    const nombre = typeof perfil.fullName === 'string' ? perfil.fullName : '';
    const foto = typeof perfil.photoURL === 'string' ? perfil.photoURL : '';
    if (!nombre && !foto) { borrarCascara(); return; }
    localStorage.setItem(CASCARA_KEY, JSON.stringify({ uid, nombre, foto, en: Date.now() }));
  } catch (_) { /* el arranque no se cae por no poder guardar un adorno */ }
}

/** La cáscara de ESTE uid, o null si es de otro, está vencida o no se puede leer. */
export function leerCascara(uid) {
  if (!uid) return null;
  try {
    const crudo = localStorage.getItem(CASCARA_KEY);
    if (!crudo) return null;
    const c = JSON.parse(crudo);
    if (!c || c.uid !== uid) return null;                       // otra cuenta: no se pinta
    if (typeof c.en !== 'number') return null;                  // sin fecha no hay plazo que valga
    if (Date.now() - c.en > CASCARA_PLAZO_MS) return null;      // vencida
    return {
      nombre: typeof c.nombre === 'string' ? c.nombre : '',
      foto: typeof c.foto === 'string' ? c.foto : '',
    };
  } catch (_) { return null; }
}

export function borrarCascara() {
  try { localStorage.removeItem(CASCARA_KEY); } catch (_) { /* nada que hacer */ }
}

// Crear el contexto
const AuthContext = createContext();

// Hook para usar el contexto fácilmente desde cualquier componente
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
}

// Provider que envuelve toda la app
// Roles a los que cada rol real puede cambiar de contexto (cuenta unificada).
// El primero es el rol "nativo". Un cliente normal no tiene contextos extra.
function rolesFor(realRole) {
  if (realRole === 'admin')          return ['admin', 'workshop_owner', 'mechanic', 'client'];
  if (realRole === 'workshop_owner') return ['workshop_owner', 'client'];
  if (realRole === 'mechanic')       return ['mechanic', 'client'];
  return ['client'];
}

const ACTIVE_ROLE_KEY = 'mekafix_activeRole';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);         // Usuario de Firebase Auth
  const [userProfile, setUserProfile] = useState(null); // Datos de Firestore (userType, mechanicProfile, etc.)
  const [loading, setLoading] = useState(true);
  // true una vez resuelto el perfil del usuario ACTUAL (exista o no el doc).
  // Los gates (SuspensionGate) esperan a esto para no mostrar contenido protegido
  // mientras el perfil aún carga o quedó el de otro usuario tras un cambio de cuenta.
  const [profileLoaded, setProfileLoaded] = useState(false);
  // true si el perfil NO se pudo leer (red caída, API frío tras reintentar). Es distinto de
  // «no hay perfil»: ProfileGate enseña un reintento en vez de mandar al selector de cuenta.
  const [profileError, setProfileError] = useState(false);
  // ╔═══════════════════════════════════════════════════════════════════════════╗
  // ║ ¿ESTE PERFIL VINO DEL API? LA GUARDA DE LOS PUESTOS NO PUEDE DECIDIR SIN   ║
  // ║ SABERLO.                                                                  ║
  // ╚═══════════════════════════════════════════════════════════════════════════╝
  //
  // `cargarPerfil` cae a Firestore ante cualquier fallo del API **o a los
  // `PERFIL_PLAZO_MS`**, y un perfil de Firestore NO TRAE CAPACIDADES por diseño.
  // Sin esta bandera, «no tiene el permiso» y «no se pudo averiguar» llegan a la
  // pantalla con la misma cara: la guarda rebotaría al staff a /home cada vez que
  // el servidor tarda cinco segundos. Un rebote que depende de la red y no de
  // quién eres es el fallo que nadie consigue reproducir.
  const [perfilDesdeApi, setPerfilDesdeApi] = useState(false);
  // Nombre y foto de la visita anterior de ESTE uid. Solo sirve para pintar la
  // cáscara del arranque; NO lleva rol ni sanción (ver el recuadro de arriba).
  const [cascara, setCascara] = useState(null);
  const [activeRole, setActiveRoleState] = useState(() => {
    try { return localStorage.getItem(ACTIVE_ROLE_KEY) || null; } catch { return null; }
  });

  const setActiveRole = useCallback((r) => {
    setActiveRoleState(r);
    try {
      if (r) localStorage.setItem(ACTIVE_ROLE_KEY, r);
      else localStorage.removeItem(ACTIVE_ROLE_KEY);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Escuchar cambios de autenticación
    let prevUid = null;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const uid = firebaseUser?.uid || null;
      const userChanged = uid !== prevUid;
      prevUid = uid;
      setUser(firebaseUser);

      if (firebaseUser) {
        // Solo en login / cambio de cuenta (no en el refresh periódico del token,
        // que dispara este callback con el MISMO uid): limpiar el perfil viejo y
        // marcar "perfil no cargado" para que los gates esperen. Sin esto, el perfil
        // del usuario anterior quedaba visible durante el getDoc (bypass de gate).
        if (userChanged) {
          setUserProfile(null);
          setProfileLoaded(false);
          setProfileError(false);
          // Por lo mismo que el perfil: un `true` heredado de la cuenta anterior
          // haría que la guarda decidiera sobre un dato que ya no es de nadie.
          setPerfilDesdeApi(false);
          // Se resuelve AQUÍ, de forma síncrona y con el uid de ESTA sesión, que
          // es lo que hace que la app tenga algo que pintar antes del primer
          // viaje al servidor. Si la guardada es de otra cuenta o está vencida,
          // `leerCascara` devuelve null y el arranque queda como estaba.
          setCascara(leerCascara(uid));
        }
        // 🔴 SIGUE SIENDO ESTA LA SESIÓN. Con el API de por medio la carga puede
        // tardar hasta `PERFIL_PLAZO_MS` más lo que cueste Firestore, en vez de
        // los milisegundos de un `getDoc`, y en ese rato alguien pudo cerrar
        // sesión y entrar con otra
        // cuenta. Una invocación vieja que llegue tarde y escriba `setUserProfile`
        // —o peor, `setProfileLoaded(true)`— pondría el perfil de la persona
        // ANTERIOR sobre la sesión nueva, o abriría los gates antes de tiempo:
        // exactamente el bypass que el bloque `userChanged` de arriba cierra.
        //
        // Se mira `prevUid` (la variable del propio efecto, que cada invocación
        // actualiza de forma SÍNCRONA nada más entrar) y no `auth.currentUser`:
        // el testigo tiene que ser de este efecto. Basarlo en el objeto de
        // Firebase lo ataría a que `currentUser` esté poblado, y donde no lo esté
        // el guard daría "esta sesión ya no vale" SIEMPRE — o sea, un perfil que
        // no se aplica nunca y toda la app en el rol de cliente por defecto.
        const vigente = () => prevUid === firebaseUser.uid;
        try {
          // Cargar el perfil del usuario (Firestore o el API, según config/migration)
          const { data, desdeApi } = await cargarPerfil(firebaseUser.uid);
          if (!vigente()) return;
          setUserProfile(data);
          setPerfilDesdeApi(desdeApi);
          // Deja lista la cáscara del PRÓXIMO arranque. Va detrás de `vigente()`
          // a propósito: una respuesta tardía de la cuenta anterior no puede
          // dejar guardado el nombre de quien ya no está en la sesión.
          guardarCascara(firebaseUser.uid, data);
          // Los dos efectos de abajo son del CAMINO VIEJO y sólo corren en él:
          // · `phoneNorm` lo deriva el propio API (viene siempre que hay teléfono),
          //   así que escribirlo a mano sobra; y escribirlo en Firestore mientras
          //   se lee Postgres es tocar la base que ya no manda.
          // · el alta sólo hace falta si la cuenta NO estaba en Postgres, y si se
          //   acaba de leer de ahí es que estaba. El 404 cae por este mismo camino
          //   (ver `cargarPerfil`), o sea que quien falta se sigue dando de alta.
          if (desdeApi) return;
          // Self-heal: normaliza el teléfono (phoneNorm) para que el taller pueda
          // encontrar al cliente al registrar un Servicio Certificado. Datos viejos
          // no tienen el campo; se llena solo en el primer login con código nuevo.
          if (data && data.phone && !data.phoneNorm) {
            const pn = String(data.phone).replace(/[^0-9]/g, '').slice(-10);
            if (pn) { try { await updateDoc(doc(db, 'users', firebaseUser.uid), { phoneNorm: pn }); } catch (e) { /* best-effort */ } }
          }
          // ALTA EN POSTGRES. En la base nueva TODO cuelga de `app_user` por
          // clave foránea: sin esta fila, guardar un carro o un pedido de esta
          // persona muere con `violates foreign key constraint`. Y no basta con
          // hacerlo en el registro — hay cuentas creadas antes de que existiera
          // el API que tampoco están.
          //
          // Va aquí y no en el login porque este callback corre TAMBIÉN al abrir
          // la app con sesión ya iniciada, que es como entra la gente que ya
          // estaba. Es idempotente (el API responde 409 si ya existe) y NO se
          // espera: aprovisionar no puede retrasar ni tumbar el arranque.
          //
          // Sólo cuando el perfil de Firestore existe: si aún no está (el
          // instante entre crear la cuenta en Auth y escribir el documento) se
          // deja para el siguiente arranque, porque un alta sin nombre ni rol
          // crearía una fila hueca que el 409 ya no dejaría rellenar.
          if (data) { ensureUserProvisioned(data); }
        } catch (error) {
          if (vigente()) { setUserProfile(null); setPerfilDesdeApi(false); setProfileError(true); }
        } finally {
          if (vigente()) setProfileLoaded(true);
        }
      } else {
        setUserProfile(null);
        setProfileError(false);
        setProfileLoaded(true);
        setPerfilDesdeApi(false);
        // Sin sesión no hay a quién saludar. Este es el borrado que de verdad
        // cubre el cierre de sesión: `logout()` llama a `signOut`, y `signOut`
        // acaba SIEMPRE aquí, venga del botón o de que Firebase invalide la
        // sesión por su cuenta.
        setCascara(null);
        borrarCascara();
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Función para refrescar el perfil (útil después de editar perfil)
  //
  // 🔴 ESTA ES LA LLAMADA DEL BLOQUEADOR. `EditProfile` guarda por el API y acto
  // seguido llama aquí; mientras esto leyera Firestore, el guardado entraba en
  // Postgres y la pantalla seguía enseñando la copia vieja: decía "guardado" y no
  // cambiaba nada. Por eso comparte `cargarPerfil` con el arranque — dos lectores
  // distintos volverían a divergir, y esa divergencia es justo el bug.
  const refreshProfile = useCallback(async () => {
    if (auth.currentUser) {
      const uid = auth.currentUser.uid;
      let data = null;
      let desdeApi = false;
      try { ({ data, desdeApi } = await cargarPerfil(uid)); } catch (_) { return; }
      // Se conserva el "sólo si hay perfil" de siempre: un fallo de lectura no
      // puede dejar la sesión sin perfil (ver el recuadro de `cargarPerfil`).
      // Y no se pisa la sesión si entre medias entró otra cuenta.
      if (data && auth.currentUser?.uid === uid) {
        setUserProfile(data);
        // Esta función es el botón de reintento del cartel «no se pudo cargar»
        // que pinta `RequireCapability`. Si la bandera no se refrescara aquí, el
        // reintento traería las capacidades y la pantalla seguiría diciendo que
        // no se pudo cargar: un reintento que funciona y no se nota.
        setPerfilDesdeApi(desdeApi);
        // El usuario acaba de cambiarse el nombre o la foto: la cáscara del
        // próximo arranque tiene que ser la nueva, no la de antes de editar.
        guardarCascara(uid, data);
      }
    }
  }, []);

  // Reintento manual tras un fallo de lectura del perfil (botón de ProfileGate).
  const retryProfile = useCallback(async () => {
    const fu = auth.currentUser;
    if (!fu) return;
    const uid = fu.uid;
    setProfileError(false);
    setProfileLoaded(false);
    try {
      const { data, desdeApi } = await cargarPerfil(uid);
      if (auth.currentUser?.uid !== uid) return;
      setUserProfile(data);
      setPerfilDesdeApi(desdeApi);
      guardarCascara(uid, data);
      if (data && !desdeApi) ensureUserProvisioned(data);
    } catch (_) {
      if (auth.currentUser?.uid === uid) { setUserProfile(null); setPerfilDesdeApi(false); setProfileError(true); }
    } finally {
      if (auth.currentUser?.uid === uid) setProfileLoaded(true);
    }
  }, []);

  // Función centralizada de logout — cierra sesión, limpia cachés + service worker
  // viejo y recarga con la versión fresca (así la app siempre queda actualizada).
  const logout = useCallback(async () => {
    setActiveRole(null);
    // Antes de nada: si `signOut` falla, esta línea ya se ejecutó y el nombre no
    // se queda guardado en un aparato del que alguien se acaba de ir.
    borrarCascara();
    try { await signOut(auth); } catch (_) {}
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (_) { /* limpieza best-effort */ }
    window.location.replace('/'); // recarga completa → última versión, sin caché
  }, [setActiveRole]);

  // Rol real del perfil y rol efectivo (contexto activo si es válido)
  const realRole = userProfile?.role || userProfile?.userType || 'client';
  const availableRoles = useMemo(() => rolesFor(realRole), [realRole]);
  const effectiveRole = (activeRole && availableRoles.includes(activeRole)) ? activeRole : realRole;

  /**
   * ¿Tiene esta cuenta el permiso suelto `nombre`?
   *
   * 🔴 EL ADMIN LO PUEDE TODO SIN QUE SE LE CONCEDA NADA, igual que
   * `hasCapability` en el servidor (`api/src/auth.ts`): un permiso es una llave
   * PEQUEÑA que se le da a quien no tiene la grande, no un requisito nuevo para
   * quien ya la tenía. Si esto se pierde, el dueño se queda fuera de sus propias
   * colas el día que se conceda la primera capacidad.
   *
   * Se mira `realRole` y no `effectiveRole` por lo mismo que `RequireRole`: el
   * contexto activo es una preferencia de VISTA, y la autorización mira quién
   * eres, no con qué lente estás mirando.
   *
   * Un `false` aquí NO significa «no le toca»: puede significar que el perfil se
   * leyó de Firestore, que no trae capacidades. Quien decida con esto tiene que
   * mirar `perfilDesdeApi` antes de tratar el `false` como una negativa.
   */
  const tieneCapacidad = useCallback((nombre) => {
    if (realRole === 'admin') return true;
    const lista = userProfile?.capabilities;
    return Array.isArray(lista) && lista.includes(nombre);
  }, [realRole, userProfile]);

  const value = useMemo(() => ({
    user,           // Firebase user (uid, email, etc.)
    userProfile,    // Firestore data (userType, mechanicProfile, etc.)
    loading,        // true mientras se carga el auth
    profileLoaded,  // true una vez resuelto el perfil del usuario actual
    profileError,   // true si el perfil no se pudo LEER (≠ no existir): pedir reintento
    retryProfile,
    // true solo si el perfil resuelto salió del API. Un perfil de Firestore no
    // trae capacidades, así que sin esto una guarda no puede distinguir «no le
    // toca» de «no se pudo saber» (ver el recuadro del estado).
    perfilDesdeApi,
    // ¿Tiene este permiso suelto? El admin, siempre.
    tieneCapacidad,
    // Nombre y foto de la visita anterior, para pintar el arranque sin esperar
    // al servidor. NO contiene rol ni suspensión: no sirve para decidir nada.
    cascara,
    // El rol se guarda en `role` (userType es legacy); estos flags leían solo
    // userType y por eso eran siempre false en cuentas modernas. Usar realRole.
    isAdmin: realRole === 'admin',
    isMechanic: realRole === 'mechanic',
    isClient: realRole === 'client',
    realRole,           // rol verdadero del perfil
    effectiveRole,      // rol del contexto activo (para navegación)
    availableRoles,     // contextos a los que puede cambiar
    activeRole,         // contexto seleccionado manualmente (o null)
    setActiveRole,      // cambiar de contexto
    refreshProfile,
    logout,
  }), [user, userProfile, loading, profileLoaded, profileError, perfilDesdeApi, cascara, activeRole, realRole, effectiveRole,
    availableRoles, tieneCapacidad, retryProfile, setActiveRole, refreshProfile, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
